import React, { useContext, memo } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import Viewport from '../../../../components/viewport';
import ErrorBoundary from '../../../../components/error-boundary';
import StoreCount from './components/store-count';
import PriceSummary from './components/price-summary';
import CategoryRank from './components/category-rank';
import UserSentiments from './components/user-sentiments';
import { IReadableProductInformationProps } from './types';
import { H2Text } from '@prisjakt/ui/text/variants';
import { PropertyString } from 'src/types';
import { ProductProperty } from 'src/components/data-list/types';
import { DataList } from 'src/components/data-list';
import { parseNodes } from 'src/components/data-list/components/parseNodes';
import { ProductInfo, SubSection } from './styles';
import { ProductFAQ } from '../product-faq';
import { ProductDescription } from '../product-description';

const ReadableProductInformation: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IReadableProductInformationProps>
>> = ({ product }) => {
  const { t, linkTo, market } = useContext(AppContext);

  if (
    !product ||
    !market.modules?.readableProductInfo ||
    !market.modules?.readableProductInfo.enabled
  ) {
    return null;
  }

  const {
    id,
    pathName,
    name,
    priceSummary,
    prices,
    category,
    popularity,
    description,
    coreProperties,
    reviewBadges,
    userReviewSummary,
    brand,
    productDescription,
    faqs,
  } = product;

  // Add brand as a custom "fake" core prop
  const brandProp: PropertyString | null =
    brand && category
      ? {
          id: 'brand',
          name: t('product', 'property.brand'),
          type: 'string',
          value: brand.name,
          pretty: brand.name,
          valueId: brand.id.toString(),
          categoryLink: linkTo('brandCategory', {
            ...category,
            brandId: brand.id,
          }),
        }
      : null;

  const brandProperties: ProductProperty[] = brandProp ? [brandProp] : [];

  const modifiedCoreProperties = brandProperties
    .concat(coreProperties?.nodes || [])
    .filter(Boolean)
    .slice(0, 10); // Some products apparently has _a lot_ of core props

  return (
    <ErrorBoundary onError={onError}>
      <div data-test="Panel" data-test-type="product-info">
        <H2Text>{t('product', 'product-info.title')}</H2Text>

        {description && (
          <Viewport mobile>
            <SubSection>
              <ProductInfo
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
              />
            </SubSection>
          </Viewport>
        )}
        <SubSection>
          {productDescription ? (
            <ProductDescription description={productDescription} />
          ) : (
            <ProductInfo>
              <PriceSummary
                productName={name}
                priceSummary={priceSummary}
                prices={prices}
              />{' '}
              <CategoryRank
                name={name}
                category={category}
                popularity={popularity}
                userReviewSummary={userReviewSummary}
              />{' '}
              <UserSentiments
                id={id}
                pathName={pathName}
                reviewBadges={reviewBadges}
              />{' '}
              <StoreCount name={name} prices={prices} />{' '}
            </ProductInfo>
          )}
        </SubSection>
        {faqs && (
          <SubSection>
            <ProductFAQ items={faqs} />
          </SubSection>
        )}
        {modifiedCoreProperties && (
          <SubSection>
            <DataList
              properties={modifiedCoreProperties.map(childNode =>
                parseNodes(childNode, t, false)
              )}
            />
          </SubSection>
        )}
      </div>
    </ErrorBoundary>
  );
};

const onError = () => null;

export default memo(ReadableProductInformation);
